/* eslint-disable @typescript-eslint/no-unsafe-member-access, no-nested-ternary */
import React from 'react';
import _sortBy from 'lodash/sortBy';
import { CalendarEventType } from '../../CalendarEventType';
import { CalendarAvailabilityType } from '../../CalendarAvailabilityType';
import { CalendarScheduleTypeEnum } from '../../CalendarScheduleTypeEnum';
import { CalendarSidebarTabTypeEnum, RecruiterCalendarSidebarTabTypeEnum } from '../../CalendarSidebarTab';
import { useCurrentUserProfile } from '../../../../app/hooks/useCurrentUserProfile';
import { useStoreState } from '../../../../models/hooks';
import { Recruiter } from '../../../../firebase/firestore/documents/recruiter';
import theme from '../../../../styles/customTheme';
import colors from '../../../../styles/colors';
import { GMAIL_COLORS, OUTLOOK_COLORS, RECRUITER_COLORS, STRIPE_GRADIENT } from '../calendarConstants';
import { moveCurrentUserIdToFirstIndex } from '../utils/calendarUtils';

type ThemeColors = Record<string, Record<string | number, string>>;

export interface UseCalendarEventStylesParams {
  administrationCalendar: boolean;
  mergedRecruiters: Recruiter[];
  activeTab?: CalendarSidebarTabTypeEnum | RecruiterCalendarSidebarTabTypeEnum;
}

export const useCalendarEventStyles = ({ administrationCalendar, mergedRecruiters, activeTab }: UseCalendarEventStylesParams) => {
  const { currentUserProfile } = useCurrentUserProfile();
  const isOperator = useStoreState((s) => s.app.user?.operator?.enabled);
  const defaultAccount = useStoreState((s) => s.app.user?.account);

  // Per-user colour helpers

  const getStyleForAvailabilityByUser = React.useCallback(
    (userId: string): string => {
      if (administrationCalendar && mergedRecruiters.length > 0) {
        const indexOfCurrentUser = mergedRecruiters.findIndex((r) => r.id === currentUserProfile?.id) % 10;
        if (indexOfCurrentUser < 0) {
          return RECRUITER_COLORS[0];
        }
        const reOrderedRecruiter = moveCurrentUserIdToFirstIndex(mergedRecruiters, indexOfCurrentUser, 0);
        const indexOfRecruiter = reOrderedRecruiter.findIndex((r) => r.id === userId) % 10;
        return RECRUITER_COLORS[indexOfRecruiter];
      }
      return RECRUITER_COLORS[0];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [administrationCalendar, mergedRecruiters, currentUserProfile?.id],
  );

  const getStyleForGmail = React.useCallback(
    (email: string | undefined): string => {
      const googleConnectedAccount = _sortBy(currentUserProfile?.googleCalendar, 'gmail');
      const indexOfEmail = googleConnectedAccount.findIndex((r) => r.gmail === email) % 5;
      return GMAIL_COLORS[indexOfEmail > 0 ? indexOfEmail : /* istanbul ignore next */ 0];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserProfile?.googleCalendar],
  );

  const getStyleForOutLook = React.useCallback(
    (email: string | undefined): string => {
      const microsoftConnectedAccount = _sortBy(currentUserProfile?.microsoftCalendar, 'microsoftMail');
      const indexOfEmail = microsoftConnectedAccount.findIndex((r) => r.microsoftMail === email) % 5;
      return OUTLOOK_COLORS[indexOfEmail > 0 ? /* istanbul ignore next */ indexOfEmail : 0];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserProfile?.microsoftCalendar],
  );

  const handleEventType = React.useCallback(
    (event: CalendarEventType) => {
      const themeColors = (theme.colors as unknown) as ThemeColors;

      // Availability slot
      if (event.resource.type === CalendarScheduleTypeEnum.AVAILABILITY) {
        const { availability } = event.resource as CalendarAvailabilityType;
        const colorCode = getStyleForAvailabilityByUser(availability.userId);

        let actBorderColor: string | undefined;
        let gradinetBackground: string | undefined;

        if (!administrationCalendar) {
          actBorderColor =
            // eslint-disable-next-line no-nested-ternary
            availability.userId === currentUserProfile?.id
              ? availability.adhoc
                ? /* istanbul ignore next */ colors.purple[700]
                : colors.blue[500]
              : colors.green[500];

          if (isOperator) {
            // eslint-disable-next-line prefer-destructuring
            actBorderColor = colors.blue[500];
            gradinetBackground =
              defaultAccount === availability.accountId
                ? /* istanbul ignore next */ colors.blue[50]
                : /* istanbul ignore next */ STRIPE_GRADIENT(colors.gray[50]);
          }
        } else {
          // istanbul ignore else
          // eslint-disable-next-line no-lonely-if
          if (event.resource.userId) {
            gradinetBackground =
              // eslint-disable-next-line no-nested-ternary
              activeTab === CalendarSidebarTabTypeEnum.RECRUITERS
                ? defaultAccount !== availability.accountId
                  ? /* istanbul ignore next */ STRIPE_GRADIENT(colors.gray[50])
                  : // istanbul ignore next
                    themeColors[colorCode][50]
                : colors.green[50];

            actBorderColor =
              // eslint-disable-next-line no-nested-ternary
              activeTab === CalendarSidebarTabTypeEnum.RECRUITERS
                ? availability.adhoc
                  ? /* istanbul ignore next */ colors.purple[700]
                  : themeColors[colorCode][500]
                : // eslint-disable-next-line no-nested-ternary
                availability.userId === currentUserProfile?.id
                ? // eslint-disable-next-line no-nested-ternary
                  /* istanbul ignore next */
                  defaultAccount === availability.accountId
                  ? availability.adhoc
                    ? /* istanbul ignore next */ colors.purple[700]
                    : colors.blue[500]
                  : // istanbul ignore next
                    colors.gray[500]
                : // istanbul ignore next
                  colors.green[500];
          }
        }

        const classNameForAvailabilitySlot =
          // eslint-disable-next-line no-nested-ternary
          administrationCalendar && activeTab === CalendarSidebarTabTypeEnum.RECRUITERS
            ? colorCode
            : availability.userId === currentUserProfile?.id
            ? 'blue'
            : 'green';

        return {
          className: `availability-slot admin-view ${classNameForAvailabilitySlot}`,
          style: {
            marginBottom: '2px',
            borderLeft: `3px solid ${actBorderColor}`,
            background: availability.adhoc ? /* istanbul ignore next */ 'rgba(123, 31, 162, 0.2)' : gradinetBackground,
          },
        };
      }

      // Group event
      if (event.resource.type === CalendarScheduleTypeEnum.GROUP_EVENT) {
        return { className: 'groupevent-slot' };
      }

      // Google / Outlook event
      if (
        event.resource.type === CalendarScheduleTypeEnum.GOOGLE_EVENT ||
        event.resource.type === CalendarScheduleTypeEnum.OUTLOOK_EVENT
      ) {
        const colorCode =
          event.resource.type === CalendarScheduleTypeEnum.GOOGLE_EVENT
            ? getStyleForGmail(event.email)
            : getStyleForOutLook(event.email);
        const isOwnEvent = event.email === event.creator?.email;

        return {
          className: 'google-event-slot',
          style: {
            backgroundColor: isOwnEvent ? themeColors[colorCode][50] : colors.white,
            marginBottom: '2px',
            fontSize: '13px',
            lineHeight: '1.5',
            minHeight: '45px',
            border: `1px solid ${isOwnEvent ? themeColors[colorCode][200] : themeColors[colorCode][300]}`,
            color: isOwnEvent ? colors.gray[600] : themeColors[colorCode][300],
          },
        };
      }

      return { className: `booking-slot ${administrationCalendar ? 'admin-booking-slot' : /* istanbul ignore next */ ''}` };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      administrationCalendar,
      activeTab,
      currentUserProfile,
      defaultAccount,
      getStyleForAvailabilityByUser,
      getStyleForGmail,
      getStyleForOutLook,
      isOperator,
    ],
  );

  return { handleEventType };
};
